import styles from 'typescript/apps/checkout_v3/components/nav_bar.module.scss';
import SquareLogoSvg from 'images/square.svg';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { MarketButton, MarketIcon, MarketLink } from '@market/react';
import { OrderSummaryModal } from 'typescript/apps/checkout_v3/components/order_summary/order_summary';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useWindowDimensions } from 'typescript/hooks/use_window_dimensions';
import * as Breakpoints from 'typescript/utilities/breakpoints';
import { useLocation, useHistory } from 'react-router-dom';
import { buildCartLink } from 'utilities/links'; // TODO: migrate to TS
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { CHECKOUT_PAGE_CLICK_EVENTS } from 'typescript/constants/tracking';

export const CART_NAV_BUTTON_TEST_ID = 'cart-nav-button';
export const CART_ITEMS_LINK_TEST_ID = 'cart-items-link';
export const DASHBOARD_NAV_BUTTON_TEST_ID = 'dashboard-nav-button';

const NavBar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const history = useHistory();

  const { matchesMinWidth } = useWindowDimensions();
  const isDesktopView = matchesMinWidth(Breakpoints.md);

  const { orderNumber, trackingData } = useCheckoutFlow();
  const { orderSummaryData, isLoading, error } = useGetOrderSummary({
    orderNumber,
  });

  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);

  const isCompletePage = pathname === CHECKOUT_ROUTES.COMPLETE;
  const isPaymentPage = pathname === CHECKOUT_ROUTES.PAYMENT;
  const backText = isPaymentPage
    ? t('checkout.label.delivery')
    : isDesktopView && t('checkout.label.cart');
  const shouldDisplayBackLink =
    !isCompletePage && !isDesktopView && !isLoading && !error;

  const cartItemCount = (orderSummaryData?.cart_summary ?? []).reduce(
    (acc, item) => {
      return acc + item.quantity;
    },
    0,
  );

  const cartItemCountKey =
    cartItemCount === 1 ? 'cart_item_count_one' : 'cart_item_count_other';

  const backToCartClickEventMap = {
    [CHECKOUT_ROUTES.DELIVERY]:
      CHECKOUT_PAGE_CLICK_EVENTS.nav_back_to_cart_on_delivery,
    [CHECKOUT_ROUTES.PAYMENT]:
      CHECKOUT_PAGE_CLICK_EVENTS.nav_back_to_delivery_on_payment,
  };

  return (
    <>
      <div className={styles.navBarContainer}>
        <div className={styles.navItemLeftAligned}>
          {!isCompletePage && (
            <MarketButton
              className={styles.backButton}
              rank="tertiary"
              data-testid={CART_NAV_BUTTON_TEST_ID}
              onClick={() => {
                const clickEvent = backToCartClickEventMap[pathname] || null;
                if (clickEvent) {
                  onCheckoutPageClick({
                    eventDesc: clickEvent,
                    trackingData,
                  });
                }
                isPaymentPage
                  ? history.push(CHECKOUT_ROUTES.DELIVERY)
                  : (window.location.href = buildCartLink());
              }}
            >
              <>
                <MarketIcon slot="icon" name="return" fidelity={16} />
                {isDesktopView && backText}
              </>
            </MarketButton>
          )}
        </div>
        <div className={styles.navItemCenterAligned}>
          <img
            src={SquareLogoSvg}
            alt="Square"
            className={styles.squareLogo}
            data-testid="square"
          />
        </div>
        <div className={styles.navItemRightAligned}>
          {isCompletePage && (
            <MarketButton
              href={'/dashboard'}
              className={styles.dashboardNavButton}
              rank="tertiary"
              data-testid={DASHBOARD_NAV_BUTTON_TEST_ID}
            >
              <>
                {isDesktopView && t('checkout.label.dashboard')}
                <MarketIcon name="continue" fidelity={16} />
              </>
            </MarketButton>
          )}
          {shouldDisplayBackLink && (
            <MarketLink
              data-testid={CART_ITEMS_LINK_TEST_ID}
              onClick={() => setShowOrderSummaryModal(true)}
            >
              {t(`checkout.label.${cartItemCountKey}`, {
                count: cartItemCount,
              })}
            </MarketLink>
          )}
        </div>
      </div>
      {shouldDisplayBackLink && showOrderSummaryModal && (
        <OrderSummaryModal setShowModal={setShowOrderSummaryModal} />
      )}
    </>
  );
};

export default NavBar;
